import { useState, useEffect, useCallback } from "react";
import ApplyModal from "./ApplyModal";
import SiteFooter from "./SiteFooter";
import api from "../utils/api";

/* ── Self-contained notify (no prop needed) ─────────────────────────────── */
function useNotify() {
  const [toast, setToast] = useState(null);
  const notify = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return [toast, notify];
}

/* ── Styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap');

  @keyframes jp-fadein   { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
  @keyframes jp-slidein  { from{opacity:0;transform:translateX(-10px)} to{opacity:1;transform:none} }
  @keyframes jp-pulseglw { 0%,100%{opacity:.35} 50%{opacity:.65} }
  @keyframes jp-shimmer  { from{background-position:-200% 0} to{background-position:200% 0} }
  @keyframes jp-toastin  { from{opacity:0;transform:translateY(12px) scale(.95)} to{opacity:1;transform:none} }

  .jp-root {
    --jp-blue: #4f8ef7;
    --jp-blue-2: rgba(79,142,247,.12);
    --jp-green: #22c55e;
    --jp-surface: rgba(255,255,255,.035);
    --jp-surface2: rgba(255,255,255,.055);
    --jp-line: rgba(255,255,255,.08);
    --jp-line2: rgba(255,255,255,.13);
    --jp-t1: #f1f5f9;
    --jp-t2: #94a3b8;
    --jp-t3: #475569;
    --jp-display: "Bricolage Grotesque", sans-serif;
    --jp-body: 'DM Sans', sans-serif;
    font-family: var(--jp-body);
    min-height: 100vh;
    background: #080c18;
    color: var(--jp-t1);
    overflow-x: hidden;
  }

  /* Toast */
  .jp-toast {
    position: fixed; bottom: 28px; right: 28px; z-index: 9999;
    padding: 13px 20px; border-radius: 12px; font-size: .875rem; font-weight: 500;
    display: flex; align-items: center; gap: 10px;
    animation: jp-toastin .3s cubic-bezier(.16,1,.3,1);
    box-shadow: 0 8px 32px rgba(0,0,0,.45); backdrop-filter: blur(12px);
    font-family: var(--jp-body);
  }
  .jp-toast.success { background: rgba(34,197,94,.18); border: 1px solid rgba(34,197,94,.35); color: #86efac; }
  .jp-toast.error   { background: rgba(239,68,68,.18);  border: 1px solid rgba(239,68,68,.35);  color: #fca5a5; }

  /* Hero */
  .jp-hero { position: relative; padding: 110px 0 80px; overflow: hidden; }
  .jp-hero-canvas { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
  .jp-hero-orb1 {
    position: absolute; width: 560px; height: 560px; border-radius: 50%;
    background: radial-gradient(circle, rgba(79,142,247,.18) 0%, transparent 70%);
    top: -180px; left: -120px; animation: jp-pulseglw 6s ease-in-out infinite;
  }
  .jp-hero-orb2 {
    position: absolute; width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(124,58,237,.14) 0%, transparent 70%);
    bottom: -100px; right: -80px; animation: jp-pulseglw 8s ease-in-out infinite reverse;
  }
  .jp-hero-grid {
    position: absolute; inset: 0;
    background-image: linear-gradient(rgba(79,142,247,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(79,142,247,.04) 1px,transparent 1px);
    background-size: 52px 52px;
    mask-image: radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%);
  }
  .jp-hero-inner {
    position: relative; z-index: 1; max-width: 1100px; margin: 0 auto; padding: 0 28px;
    display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 48px;
  }
  @media(max-width:780px){.jp-hero-inner{grid-template-columns:1fr}}

  .jp-hero-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 5px 14px 5px 8px; border-radius: 100px;
    background: rgba(79,142,247,.1); border: 1px solid rgba(79,142,247,.25);
    font-size: .72rem; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
    color: var(--jp-blue); margin-bottom: 20px; width: fit-content;
    animation: jp-fadein .5s both;
  }
  .jp-eyebrow-dot {
    width: 6px; height: 6px; border-radius: 50%; background: var(--jp-blue);
    animation: jp-pulseglw 2s infinite;
  }
  .jp-hero-h1 {
    font-family: var(--jp-display); font-size: clamp(2.6rem,5.5vw,4rem);
    font-weight: 800; line-height: 1.08; letter-spacing: -.03em; color: var(--jp-t1);
    margin: 0 0 20px; animation: jp-fadein .6s .08s both;
  }
  .jp-hero-h1 em {
    font-style: normal;
    background: linear-gradient(135deg,#60a5fa,#818cf8,#a78bfa);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .jp-hero-sub {
    font-size: 1.05rem; color: var(--jp-t2); line-height: 1.7;
    max-width: 520px; margin: 0 0 28px; animation: jp-fadein .6s .15s both;
  }
  .jp-hero-perks { display: flex; flex-wrap: wrap; gap: 8px; animation: jp-fadein .6s .22s both; }
  .jp-hero-perk {
    display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px;
    border-radius: 100px; background: var(--jp-surface); border: 1px solid var(--jp-line2);
    font-size: .78rem; color: var(--jp-t2); font-weight: 500;
  }
  .jp-hero-perk svg { color: var(--jp-green); }
  .jp-hero-stats { display: flex; flex-direction: column; gap: 14px; animation: jp-fadein .6s .3s both; }
  .jp-hero-stat {
    background: var(--jp-surface); border: 1px solid var(--jp-line2); border-radius: 16px;
    padding: 20px 28px; text-align: center; min-width: 128px; backdrop-filter: blur(8px);
    transition: border-color .2s, background .2s;
  }
  .jp-hero-stat:hover { border-color: rgba(79,142,247,.35); background: var(--jp-surface2); }
  .jp-hero-stat-n {
    font-family: var(--jp-display); font-size: 2rem; font-weight: 800;
    color: var(--jp-t1); line-height: 1;
  }
  .jp-hero-stat-l { font-size: .7rem; color: var(--jp-t3); margin-top: 4px; text-transform: uppercase; letter-spacing: .08em; }

  /* Filter bar */
  .jp-filters {
    position: sticky; top: 0; z-index: 40;
    background: rgba(8,12,24,.88); backdrop-filter: blur(20px) saturate(1.4);
    border-bottom: 1px solid var(--jp-line); padding: 14px 0;
  }
  .jp-filters-inner {
    max-width: 1100px; margin: 0 auto; padding: 0 28px;
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  }
  .jp-search-wrap { position: relative; flex: 1; min-width: 200px; }
  .jp-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: var(--jp-t3); pointer-events: none; }
  .jp-search {
    width: 100%; padding: 10px 38px; background: var(--jp-surface);
    border: 1px solid var(--jp-line2); border-radius: 10px; color: var(--jp-t1);
    font-family: var(--jp-body); font-size: .875rem; outline: none;
    transition: border-color .18s, background .18s; box-sizing: border-box;
  }
  .jp-search::placeholder { color: var(--jp-t3); }
  .jp-search:focus { border-color: var(--jp-blue); background: var(--jp-surface2); }
  .jp-search-clear {
    position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
    background: none; border: none; color: var(--jp-t3); cursor: pointer; padding: 4px;
    border-radius: 4px; display: flex; align-items: center; transition: color .14s;
  }
  .jp-search-clear:hover { color: var(--jp-t1); }
  .jp-filter-pills { display: flex; gap: 6px; flex-wrap: wrap; }
  .jp-pill {
    padding: 7px 14px; border-radius: 100px; background: var(--jp-surface);
    border: 1px solid var(--jp-line2); color: var(--jp-t2); font-size: .78rem;
    font-weight: 600; cursor: pointer; font-family: var(--jp-body);
    transition: all .16s; white-space: nowrap;
  }
  .jp-pill:hover { background: var(--jp-surface2); color: var(--jp-t1); }
  .jp-pill.active { background: var(--jp-blue-2); border-color: rgba(79,142,247,.45); color: var(--jp-blue); }
  .jp-type-select {
    padding: 8px 14px; border-radius: 10px; background: var(--jp-surface);
    border: 1px solid var(--jp-line2); color: var(--jp-t2);
    font-family: var(--jp-body); font-size: .8rem; outline: none; cursor: pointer;
  }
  .jp-type-select:focus { border-color: var(--jp-blue); }

  /* Layout */
  .jp-section { max-width: 1100px; margin: 0 auto; padding: 36px 28px 100px; }
  .jp-count-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .jp-count { font-size: .82rem; color: var(--jp-t3); font-weight: 500; }
  .jp-count strong { color: var(--jp-t2); }
  .jp-layout { display: grid; grid-template-columns: 380px 1fr; gap: 24px; }
  @media(max-width:900px){.jp-layout{grid-template-columns:1fr}}

  /* List */
  .jp-list { display: flex; flex-direction: column; gap: 8px; }
  .jp-list-item {
    width: 100%; text-align: left; background: var(--jp-surface);
    border: 1px solid var(--jp-line); border-radius: 16px; padding: 18px 20px;
    cursor: pointer; font-family: var(--jp-body); transition: all .18s;
    position: relative; overflow: hidden;
    animation: jp-slidein .25s both;
  }
  .jp-list-item:nth-child(1){animation-delay:.03s}
  .jp-list-item:nth-child(2){animation-delay:.07s}
  .jp-list-item:nth-child(3){animation-delay:.11s}
  .jp-list-item:nth-child(4){animation-delay:.15s}
  .jp-list-item:nth-child(5){animation-delay:.19s}
  .jp-list-item:nth-child(n+6){animation-delay:.22s}
  .jp-list-item::before {
    content:''; position:absolute; left:0; top:0; bottom:0; width:3px;
    background:var(--jp-blue); opacity:0; transition:opacity .18s; border-radius:0 2px 2px 0;
  }
  .jp-list-item:hover { border-color: var(--jp-line2); background: var(--jp-surface2); }
  .jp-list-item:hover::before { opacity: .6; }
  .jp-list-item.active { border-color: rgba(79,142,247,.4); background: rgba(79,142,247,.07); }
  .jp-list-item.active::before { opacity: 1; }

  .jp-list-item-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; }
  .jp-dept-tag {
    display: inline-block; padding: 3px 9px; border-radius: 6px;
    font-size: .68rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
    background: rgba(79,142,247,.12); border: 1px solid rgba(79,142,247,.22); color: #93c5fd;
  }
  .jp-open-badge {
    display: flex; align-items: center; gap: 5px;
    font-size: .68rem; font-weight: 600; color: var(--jp-green);
  }
  .jp-open-badge::before {
    content:''; width:6px; height:6px; border-radius:50%; background: var(--jp-green);
    box-shadow: 0 0 6px rgba(34,197,94,.5);
  }
  .jp-list-title { font-family: var(--jp-display); font-size: .975rem; font-weight: 700; color: var(--jp-t1); margin-bottom: 8px; line-height: 1.3; }
  .jp-list-meta { display: flex; flex-wrap: wrap; gap: 10px; }
  .jp-meta-item { display: flex; align-items: center; gap: 5px; font-size: .75rem; color: var(--jp-t3); }

  /* Detail */
  .jp-detail-sticky { position: sticky; top: 70px; height: fit-content; overflow-y: auto; }
  .jp-detail-sticky::-webkit-scrollbar { width: 3px; }
  .jp-detail-sticky::-webkit-scrollbar-thumb { background: var(--jp-line2); border-radius: 2px; }

  .jp-detail-placeholder {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 64px 32px; border: 1px dashed var(--jp-line2); border-radius: 16px;
    color: var(--jp-t3); gap: 14px; text-align: center; min-height: 280px;
  }
  .jp-detail-placeholder p { font-size: .875rem; }
  .jp-detail-panel {
    background: var(--jp-surface); border: 1px solid var(--jp-line2); border-radius: 16px;
    overflow: hidden; animation: jp-fadein .22s both;
  }
  .jp-detail-head {
    padding: 28px 28px 24px;
    background: linear-gradient(135deg,rgba(79,142,247,.08),rgba(124,58,237,.05));
    border-bottom: 1px solid var(--jp-line);
  }
  .jp-detail-dept { margin-bottom: 10px; }
  .jp-detail-title {
    font-family: var(--jp-display); font-size: 1.5rem; font-weight: 800;
    color: var(--jp-t1); margin: 0 0 12px; line-height: 1.2; letter-spacing: -.02em;
  }
  .jp-detail-meta { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
  .jp-salary-badge {
    display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 100px;
    background: rgba(34,197,94,.1); border: 1px solid rgba(34,197,94,.25);
    font-size: .78rem; font-weight: 600; color: #86efac;
  }
  .jp-apply-btn {
    width: 100%; padding: 13px 24px; border-radius: 10px;
    background: linear-gradient(135deg,#4f8ef7,#7c3aed); border: none; color: #fff;
    font-family: var(--jp-body); font-size: .9rem; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: opacity .18s, transform .16s; box-shadow: 0 4px 20px rgba(79,142,247,.3);
    letter-spacing: -.01em;
  }
  .jp-apply-btn:hover { opacity: .88; transform: translateY(-1px); }
  .jp-apply-btn:active { transform: none; }

  .jp-detail-body { padding: 24px 28px; }
  .jp-section-label {
    font-size: .69rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
    color: var(--jp-t3); margin-bottom: 10px;
  }
  .jp-detail-desc { font-size: .9rem; color: var(--jp-t2); line-height: 1.8; margin: 0 0 24px; }
  .jp-req-list { list-style: none; padding: 0; margin: 0 0 24px; display: flex; flex-direction: column; gap: 8px; }
  .jp-req-item { display: flex; align-items: flex-start; gap: 10px; font-size: .875rem; color: var(--jp-t2); line-height: 1.55; }
  .jp-req-icon {
    width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0; margin-top: 1px;
    background: rgba(34,197,94,.12); border: 1px solid rgba(34,197,94,.25);
    display: flex; align-items: center; justify-content: center; color: var(--jp-green);
  }
  .jp-auth-prompt {
    padding: 16px 20px; background: rgba(79,142,247,.07); border: 1px solid rgba(79,142,247,.2);
    border-radius: 10px; margin-top: 8px;
  }
  .jp-auth-prompt p { font-size: .85rem; color: var(--jp-t2); margin: 0 0 12px; }
  .jp-auth-actions { display: flex; gap: 8px; }

  /* Buttons */
  .jp-btn-primary {
    padding: 8px 18px; border-radius: 8px;
    background: linear-gradient(135deg,#4f8ef7,#7c3aed); border: none; color: #fff;
    font-family: var(--jp-body); font-size: .8rem; font-weight: 700; cursor: pointer;
    transition: opacity .16s;
  }
  .jp-btn-primary:hover { opacity: .85; }
  .jp-btn-ghost {
    padding: 8px 18px; border-radius: 8px; background: var(--jp-surface);
    border: 1px solid var(--jp-line2); color: var(--jp-t2);
    font-family: var(--jp-body); font-size: .8rem; font-weight: 600; cursor: pointer;
    transition: border-color .16s, color .16s;
  }
  .jp-btn-ghost:hover { border-color: rgba(79,142,247,.4); color: var(--jp-t1); }

  /* Skeleton */
  .jp-skeleton-grid { display: flex; flex-direction: column; gap: 10px; }
  .jp-skeleton-card { background: var(--jp-surface); border: 1px solid var(--jp-line); border-radius: 16px; padding: 22px 24px; }
  .sk-line {
    border-radius: 6px; height: 13px; margin-bottom: 10px;
    background: linear-gradient(90deg,var(--jp-line) 0%,var(--jp-line2) 50%,var(--jp-line) 100%);
    background-size: 200% 100%; animation: jp-shimmer 1.6s infinite;
  }

  /* Empty */
  .jp-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 72px 32px; text-align: center; border: 1px dashed var(--jp-line2); border-radius: 16px; color: var(--jp-t3); }
  .jp-empty-icon { width: 60px; height: 60px; border-radius: 16px; background: var(--jp-surface); border: 1px solid var(--jp-line2); display: flex; align-items: center; justify-content: center; opacity: .6; }
  .jp-empty h3 { font-family: var(--jp-display); color: var(--jp-t2); font-size: 1.1rem; margin: 0; }
  .jp-empty p  { font-size: .875rem; margin: 0; }

  /* Mobile apply bar */
  .jp-mobile-apply {
    display: none; position: fixed; bottom: 0; left: 0; right: 0; z-index: 50;
    padding: 16px 20px; background: rgba(8,12,24,.95); backdrop-filter: blur(20px);
    border-top: 1px solid var(--jp-line2);
  }
  @media(max-width:900px){.jp-mobile-apply{display:block}}
`;

export default function JobsPage({
  user: currentUser,
  setShowAuth,
  onUserUpdate,
}) {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);

  /* ── Bug fix: ApplyModal state is self-contained here ── */
  const [applyJob, setApplyJob] = useState(null);
  const [toast, notify] = useNotify();

  useEffect(() => {
    const id = "jp-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  useEffect(() => {
    api
      .get("/jobs")
      .then(({ data }) => setJobs(data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  const depts = ["All", ...new Set(jobs.map((j) => j.department))];
  const types = ["All", ...new Set(jobs.map((j) => j.type))];

  const filtered = jobs.filter((j) => {
    const matchDept = filter === "All" || j.department === filter;
    const matchType = typeFilter === "All" || j.type === typeFilter;
    const matchSearch =
      !search ||
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      j.department.toLowerCase().includes(search.toLowerCase()) ||
      j.location.toLowerCase().includes(search.toLowerCase());
    return matchDept && matchType && matchSearch;
  });

  const selected = jobs.find((j) => j._id === selectedJob) || null;

  const handleApply = (job) => {
    if (!currentUser) {
      setShowAuth("login");
      return;
    }
    setApplyJob(job);
  };

  const LocIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
  const ClkIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
  const SalIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
  const ChkIcon = () => (
    <svg
      width="9"
      height="9"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  return (
    <div className="jp-root">
      {/* Toast */}
      {toast && (
        <div className={`jp-toast ${toast.type}`}>
          {toast.type === "success" ? (
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          {toast.msg}
        </div>
      )}

      {/* Apply Modal — owned here, no external prop needed */}
      {applyJob && currentUser && (
        <ApplyModal
          job={applyJob}
          user={currentUser}
          onClose={() => setApplyJob(null)}
          notify={notify}
          onUserUpdate={onUserUpdate}
        />
      )}

      {/* ── Hero ── */}
      <section className="jp-hero">
        <div className="jp-hero-canvas">
          <div className="jp-hero-orb1" />
          <div className="jp-hero-orb2" />
          <div className="jp-hero-grid" />
        </div>
        <div className="jp-hero-inner">
          <div>
            <div className="jp-hero-eyebrow">
              <span className="jp-eyebrow-dot" />
              We're Hiring
            </div>
            <h1 className="jp-hero-h1">
              Build the Future
              <br />
              <em>With Us.</em>
            </h1>
            <p className="jp-hero-sub">
              A team of engineers, designers, and builders who ship real
              products to real users. Remote-friendly, high-trust, high-output.
            </p>
            <div className="jp-hero-perks">
              {[
                "Remote-friendly",
                "Competitive salary",
                "Latest tech stack",
                "High-trust culture",
                "Equity options",
              ].map((p) => (
                <span key={p} className="jp-hero-perk">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {p}
                </span>
              ))}
            </div>
          </div>

          <div className="jp-hero-stats">
            {[
              { n: loading ? "—" : jobs.length, l: "Open Roles" },
              {
                n: loading ? "—" : Math.max(0, depts.length - 1) || "—",
                l: "Departments",
              },
              { n: "Remote", l: "Work Style" },
            ].map(({ n, l }) => (
              <div key={l} className="jp-hero-stat">
                <div className="jp-hero-stat-n">{n}</div>
                <div className="jp-hero-stat-l">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sticky filters ── */}
      <div className="jp-filters">
        <div className="jp-filters-inner">
          <div className="jp-search-wrap">
            <svg
              className="jp-search-icon"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="jp-search"
              placeholder="Search roles, teams, skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="jp-search-clear" onClick={() => setSearch("")}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="jp-filter-pills">
            {depts.map((d) => (
              <button
                key={d}
                className={`jp-pill${filter === d ? " active" : ""}`}
                onClick={() => setFilter(d)}
              >
                {d}
              </button>
            ))}
          </div>

          <select
            className="jp-type-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {types.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Listings ── */}
      <div className="jp-section">
        {loading && (
          <div className="jp-skeleton-grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="jp-skeleton-card">
                <div className="sk-line" style={{ width: "28%", height: 10 }} />
                <div
                  className="sk-line"
                  style={{ width: "62%", height: 18, marginTop: 10 }}
                />
                <div className="sk-line" style={{ width: "46%", height: 11 }} />
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="jp-empty">
            <div className="jp-empty-icon">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
              </svg>
            </div>
            <h3>No roles found</h3>
            <p>Try adjusting your search or filters.</p>
            <button
              className="jp-btn-ghost"
              style={{ marginTop: 4 }}
              onClick={() => {
                setSearch("");
                setFilter("All");
                setTypeFilter("All");
              }}
            >
              Clear filters
            </button>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <>
            <div className="jp-count-row">
              <span className="jp-count">
                <strong>{filtered.length}</strong> role
                {filtered.length !== 1 ? "s" : ""} found
              </span>
              {(filter !== "All" || typeFilter !== "All" || search) && (
                <button
                  className="jp-btn-ghost"
                  style={{ padding: "5px 12px", fontSize: ".75rem" }}
                  onClick={() => {
                    setSearch("");
                    setFilter("All");
                    setTypeFilter("All");
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="jp-layout">
              {/* Job list */}
              <div className="jp-list">
                {filtered.map((job) => (
                  <button
                    key={job._id}
                    className={`jp-list-item${selectedJob === job._id ? " active" : ""}`}
                    onClick={() =>
                      setSelectedJob(selectedJob === job._id ? null : job._id)
                    }
                  >
                    <div className="jp-list-item-top">
                      <span className="jp-dept-tag">{job.department}</span>
                      <span className="jp-open-badge">Open</span>
                    </div>
                    <div className="jp-list-title">{job.title}</div>
                    <div className="jp-list-meta">
                      <span className="jp-meta-item">
                        <LocIcon />
                        {job.location}
                      </span>
                      <span className="jp-meta-item">
                        <ClkIcon />
                        {job.type}
                      </span>
                      {job.salary && (
                        <span className="jp-meta-item">
                          <SalIcon />
                          {job.salary}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Detail panel */}
              <div className="jp-detail-sticky">
                {!selected ? (
                  <div className="jp-detail-placeholder">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      style={{ opacity: 0.35 }}
                    >
                      <rect x="2" y="7" width="20" height="14" rx="2" />
                      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                    </svg>
                    <p>Select a role to view details</p>
                  </div>
                ) : (
                  <div className="jp-detail-panel">
                    <div className="jp-detail-head">
                      <div className="jp-detail-dept">
                        <span className="jp-dept-tag">
                          {selected.department}
                        </span>
                      </div>
                      <h2 className="jp-detail-title">{selected.title}</h2>
                      <div className="jp-detail-meta">
                        <span className="jp-meta-item">
                          <LocIcon />
                          {selected.location}
                        </span>
                        <span className="jp-meta-item">
                          <ClkIcon />
                          {selected.type}
                        </span>
                        {selected.salary && (
                          <span className="jp-salary-badge">
                            <SalIcon />
                            {selected.salary}
                          </span>
                        )}
                      </div>
                      <button
                        className="jp-apply-btn"
                        onClick={() => handleApply(selected)}
                      >
                        Apply Now
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </div>

                    <div className="jp-detail-body">
                      <div className="jp-section-label">About this role</div>
                      <p className="jp-detail-desc">{selected.description}</p>

                      {selected.requirements?.length > 0 && (
                        <>
                          <div className="jp-section-label">Requirements</div>
                          <ul className="jp-req-list">
                            {selected.requirements.map((r, i) => (
                              <li key={i} className="jp-req-item">
                                <span className="jp-req-icon">
                                  <ChkIcon />
                                </span>
                                {r}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}

                      {!currentUser && (
                        <div className="jp-auth-prompt">
                          <p>
                            Sign in or create a free account to apply for this
                            role.
                          </p>
                          <div className="jp-auth-actions">
                            <button
                              className="jp-btn-primary"
                              onClick={() => setShowAuth("register")}
                            >
                              Get Started
                            </button>
                            <button
                              className="jp-btn-ghost"
                              onClick={() => setShowAuth("login")}
                            >
                              Sign In
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile sticky apply bar */}
      {selected && (
        <div className="jp-mobile-apply">
          <button
            className="jp-apply-btn"
            onClick={() => handleApply(selected)}
          >
            Apply for {selected.title}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}
